import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, delay, map, of, shareReplay } from 'rxjs';

import { ApiDealers } from '@core';
import { ApiCity, BaseQueryParams } from '@shared';
import { buildQueryParams } from '@shared/utils';

type ApiCitiesStateData = {
    id: number;
    city: string;
    state: string;
    abbreviation?: string;
    region: string;
    country: string;
    isManual?: boolean;
};

type ApiCitiesStateDataResponse = {
    data: ApiCitiesStateData[];
    meta: {
        timestamp: string;
        statusCode: number;
        pagination: {
            totalCount: number;
            page: number;
            limit: number;
            totalPages: number;
            hasNextPage: boolean;
            hasPreviousPage: boolean;
        };
    };
};

/**
 * This api service is responsible in performing api calls for the create user steps flow
 */
@Injectable({
    providedIn: 'root',
})
export class CreateUserStepsApiService {
    /**
     * Inject http client for api calls.
     */
    private readonly _http = inject(HttpClient);

    /**
     * Base endpoint for cities/state lookups.
     *
     * NOTE: The backend uses `/api/cities-state-data` for the city/state/region dataset.
     */
    private readonly _citiesStateEndpoint = '/api/cities-state-data';

    /**
     * Cache the lookup dataset to avoid refetching on every autocomplete search/click.
     */
    private _citiesCache$?: Observable<ApiCity[]>;

    /**
     * Get all dealers
     */
    public getDealers(): Observable<ApiDealers[]> {
        // it should return a list of dealers but based on the user role, idk how they'll work it out, either I'll do it in the frontend or they handle it in the backend
        return of([
            { dealerId: 'dealer1', dealerName: 'Dealer 1' },
            { dealerId: 'dealer2', dealerName: 'Dealer 2' },
            { dealerId: 'dealer3', dealerName: 'Dealer 3' },
        ]).pipe(delay(1000));
    }

    /**
     * Retrieves a paginated, cached list of cities (sorted alphabetically by default);
     * subsequent calls ignore new `qParams` once the cache is populated.
     * On error, logs to console and resolves to an empty array instead of throwing.
     *
     * @param qParams - Optional query parameters merged over the defaults (`sortBy: 'city'`, `sortOrder: 'asc'`); ignored once cached.
     * @returns A shared, replayed Observable of cities. Note: `zipCode` is always `''` since the source endpoint doesn't provide it.
     */
    public getCities(qParams?: BaseQueryParams): Observable<ApiCity[]> {
        if (!this._citiesCache$) {
            const params = buildQueryParams({
                sortBy: 'city',
                sortOrder: 'asc',
                ...qParams,
            });

            this._citiesCache$ = this._http
                .get<ApiCitiesStateDataResponse>(this._citiesStateEndpoint, { params })
                .pipe(
                    map((response) =>
                        response.data.map((record): ApiCity => ({
                            city: record.city,
                            country: record.country,
                            state: record.state,
                            // The dataset endpoint example doesn't include `zipCode`; keep it empty for now.
                            zipCode: '',
                            region: record.region,
                        })),
                    ),
                    catchError((error) => {
                        console.error('Error fetching /api/cities-state-data: ', error);
                        return of([] as ApiCity[]);
                    }),
                    shareReplay(1),
                );
        }

        return this._citiesCache$;
    }

    /**
     * Searches cities whose name starts with the given search term (case-insensitive).
     * If `search` is empty or whitespace-only, returns the full cached city list via `getCities()` instead.
     * On error, logs to console and resolves to an empty array instead of throwing.
     *
     * @param search - The search term to match against the start of a city's name.
     * @returns An Observable of matching cities, sorted alphabetically. Note: `zipCode` is always `''` since the source endpoint doesn't provide it.
     */
    public searchCity(search: string): Observable<ApiCity[]> {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return this.getCities();
        }

        const params = buildQueryParams({
            filters: {
                city: {
                    _istartswith: normalizedSearch,
                },
            },
            sortBy: 'city',
            sortOrder: 'ASC',
        });

        return this._http
            .get<ApiCitiesStateDataResponse>(this._citiesStateEndpoint, { params })
            .pipe(
                map((response) =>
                    response.data.map((record): ApiCity => ({
                        city: record.city,
                        country: record.country,
                        state: record.state,
                        zipCode: '',
                        region: record.region,
                    })),
                ),
                catchError((error) => {
                    console.error('Error searching /api/cities-state-data: ', error);
                    return of([] as ApiCity[]);
                }),
            );
    }

    /**
     * Check email availability
     */
    public checkEmailAvailability(email: string): Observable<boolean> {
        return of(email !== 'test@example.com').pipe(delay(400));
    }
}
